<template>
  <div class="callback-container">
    <div v-if="loading" class="loading">
      <h2>正在处理微信登录...</h2>
      <div class="spinner"></div>
    </div>
    <div v-else-if="error" class="error">
      <h2>登录失败</h2>
      <p>{{ error }}</p>
      <button type="button" @click="goLoginAgain">重新扫码登录</button>
    </div>
    <div v-else class="success">
      <h2>登录成功</h2>
      <p>跳转中...</p>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import authManager from '@/utils/auth'
import { handleGuestClaimAfterAuth, stashGuestClaimContext, readGuestClaimContext } from '@/utils/examClaim'
import { getStoredSid } from '@/utils/guestExam'
import {
  decodeWechatQRRedirectPath,
  parseWechatQRState,
  clearWechatSkipAutoLogin,
  goToLogin
} from '@/utils/loginFlow'
import { resolvePostLoginRoute } from '@/utils/referralAuth'
import {
  accountSetupRoute,
  markAccountSetupPrompted,
  requiresInitialAccountSetup,
  shouldForceAccountSetupOnLogin
} from '@/utils/accountSetup'

function buildClaimRoute(qrState, routeQuery) {
  const ctx = readGuestClaimContext()
  const examId = String(qrState.examId || routeQuery?.examId || ctx?.examId || '')
  let sid = qrState.sid || routeQuery?.sid || ctx?.sid || ''
  if (!sid && examId) {
    sid = getStoredSid(examId) || ''
  }
  return {
    query: {
      sid,
      examId,
      href: qrState.href || routeQuery?.href || ''
    }
  }
}

function formatCallbackError(payload, fallback) {
  if (!payload || typeof payload !== 'object') {
    return fallback
  }
  const msg = String(payload.message || '').trim()
  const info = String(payload.info || '').trim()
  if (msg && info && msg !== info && !msg.includes(info)) {
    return `${msg}（${info}）`
  }
  return msg || info || fallback
}

export default {
  name: 'WeChatCallbackView',
  data() {
    return {
      loading: true,
      error: null,
      handled: false
    }
  },
  async created() {
    if (this.handled) return
    this.handled = true
    try {
      const code = this.$route.query.code
      const state = this.$route.query.state
      const qrState = parseWechatQRState(state)

      if (!code) {
        const href = decodeWechatQRRedirectPath(state)
        this.$router.replace(href || '/')
        return
      }

      if (qrState.sid && qrState.examId) {
        stashGuestClaimContext({ sid: qrState.sid, examId: qrState.examId })
      }

      const claimRoute = buildClaimRoute(qrState, this.$route.query)
      const response = await axios.get('/api/v1/wechat/callback', {
        params: { code, state }
      })
      const payload = response?.data || {}

      if (payload.code === 200 && payload.data?.accessToken) {
        // 新用户未完善登录名/密码：本次登录直接跳转完善页
        // （markAccountSetupPrompted 延迟到确定跳转时再设置：若走测评 claim 流程
        //   直接进入报告页，此时不应抑制 App.login 的完善引导弹窗）
        const needSetup = requiresInitialAccountSetup(payload.data)
        clearWechatSkipAutoLogin()
        await authManager.handleLoginSuccess(payload.data)
        this.$root.login(payload.data)

        try {
          const claimStatus = await handleGuestClaimAfterAuth(this.$router, claimRoute)
          if (claimStatus === 'claimed' || claimStatus === 'recharge') {
            return
          }
          if (claimStatus === 'none' && claimRoute.query.sid) {
            const msg = '登录成功，但未能生成报告（答题会话可能已过期）。请返回测评页重新提交后再登录。'
            this.error = msg
            this.$message.warning(msg)
            return
          }
        } catch (claimErr) {
          const msg = claimErr.message || '生成报告失败'
          this.error = msg
          this.$message.error(msg)
          return
        }

        this.$message.success('登录成功')
        if (payload.couponBindStatus === 'failed' && payload.couponBindMessage) {
          this.$message.warning(`礼券绑定失败：${payload.couponBindMessage}`)
        } else if (payload.couponBindStatus === 'ok') {
          this.$message.success('礼券已绑定成功')
        }

        const redirectUrl = qrState.href || payload.redirectURL || '/'
        const normalized =
          typeof redirectUrl === 'string' && redirectUrl.startsWith('/') ? redirectUrl : '/'

        // 未完善且无明确回跳（仅首页）：强制完善页；带着具体 href 则直接去目标页
        if (needSetup && shouldForceAccountSetupOnLogin(normalized)) {
          markAccountSetupPrompted()
          const setupTarget = accountSetupRoute(normalized)
          setTimeout(() => {
            this.$router.replace(setupTarget)
          }, 500)
          return
        }
        if (needSetup) {
          markAccountSetupPrompted()
        }

        const target = await resolvePostLoginRoute(normalized, {
          skipReferralForExam: normalized.startsWith('/exam/') || !!(qrState.sid && qrState.examId)
        })
        setTimeout(() => {
          if (typeof target === 'string') {
            if (target.startsWith('/')) {
              this.$router.replace(target)
            } else {
              window.location.href = target
            }
          } else {
            this.$router.replace(target)
          }
        }, 500)
        return
      }

      throw new Error(
        formatCallbackError(payload, '微信登录失败，请重新扫码（授权码只能使用一次）')
      )
    } catch (err) {
      console.error('WeChat callback error:', err)
      const fromAxios = err?.response?.data
      this.error = formatCallbackError(
        fromAxios,
        err?.message || '微信登录失败，请重新扫码登录'
      )
    } finally {
      this.loading = false
    }
  },
  methods: {
    goLoginAgain() {
      const state = this.$route.query.state
      const href = decodeWechatQRRedirectPath(state) || '/'
      goToLogin(this.$router, href)
    }
  }
}
</script>

<style scoped>
.callback-container {
  max-width: 600px;
  margin: 100px auto;
  padding: 20px;
  text-align: center;
}

.loading, .error, .success {
  padding: 20px;
  border-radius: 8px;
  background-color: #f8f9fa;
}

.spinner {
  width: 40px;
  height: 40px;
  margin: 20px auto;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #42b983;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error {
  color: #dc3545;
}

.error button {
  margin-top: 20px;
  padding: 10px 20px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.error button:hover {
  background-color: #c82333;
}

.success {
  color: #28a745;
}
</style>
